<template>
    <!-- Modern Vertical Gift Card -->
    <div class="flex flex-col h-full">
        <!-- Image Section -->
        <div class="relative aspect-w-16 aspect-h-9 bg-gray-100">
            <div class="relative flex items-center justify-center h-48">
                <div :id="'loadingDiv' + gift.id" class="loadingio-spinner-eclipse-fdr3b2jliw9">
                    <div class="ldio-xgxu3y3rvis">
                        <div></div>
                    </div>
                </div>
            </div>
            <div :id="'imageDiv' + gift.id" hidden class="absolute inset-0">
                <a v-if="gift.url" :href="safeUrl" target="_blank" rel="noopener noreferrer" class="block w-full h-full">
                    <img
@load="isLoading = false" :src="giftImage" :alt="gift.name"
                        class="w-full h-full object-cover"
/>
                </a>
                <img
v-else @load="isLoading = false" :src="giftImage" :alt="gift.name"
                    class="w-full h-full object-cover"
/>
            </div>

            <!-- Price Badge -->
            <div v-if="gift.price" class="absolute top-1 right-1">
                <span
                    class="inline-flex items-center px-2.5 py-1 rounded-md text-sm font-semibold bg-green-100 text-green-800 shadow-lg"
>
                    ${{ gift.price }}
                </span>
            </div>

            <!-- Domain Badge -->
            <div v-if="gift.url" class="absolute top-1 left-1">
                <span
                    class="inline-flex items-center px-2.5 py-1 rounded-md text-xs bg-white/90 text-gray-700 backdrop-blur shadow-lg"
>
                    {{ getDomainName(gift.url) }}
                </span>
            </div>
        </div>

        <!-- Content Section -->
        <div class="flex-1 p-2 flex flex-col">
            <!-- Title -->
            <h3 class="font-semibold text-gray-900 mb-2 line-clamp-2" :class="dynamicTextSize">
                {{ gift.name }}
            </h3>

            <!-- Description -->
            <p class="text-sm text-gray-600 line-clamp-3 mb-3 flex-1">
                {{ gift.description }}
            </p>

            <!-- Created Timestamp -->
            <div class="flex items-center text-xs text-gray-500 mb-3 mt-auto">
                <svg class="w-4 h-4 mr-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
/>
                </svg>
                <span>Added {{ formatDate(gift.createdAt) }}</span>
            </div>

            <!-- Action Buttons -->
            <div class="grid grid-cols-3 gap-2">
                <button
type="button" @click="$router.push({ name: 'GiftAssign', params: { id: gift.id } })"
                    class="col-span-1 px-3 py-2 text-xs font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors"
>
                    Give
                </button>
                <button
type="button" @click="$router.push({ name: 'GiftEdit', params: { id: gift.id } })"
                    class="col-span-1 px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
>
                    Edit
                </button>
                <button
type="button" @click="deleteGift()"
                    class="col-span-1 px-3 py-2 text-xs font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
>
                    Delete
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { API, graphqlOperation } from "aws-amplify";

import { onDeleteGift } from "../graphql/subscriptions";

import { getGift } from '../graphql/queries';

import { deleteGift } from '../graphql/mutations';
import { deleteGiftFriend } from '../graphql/customMutations';

export default {
    name: "GiftItem",
    props: {
        gift: {
            type: Object,
            default() {
                return {}
            }
        },
    },
    components: {
    },
    data() {
        return {
            image: "",
            isLoading: true,
            giftUpdateSub: null,
            giftDeleteSub: null,
        };
    },
    setup() {

    },
    unmounted() {
        this.giftUpdateSub.unsubscribe();
        this.giftDeleteSub.unsubscribe();
    },
    created() {
        this.getImage(this.gift.image);
        this.subscribe();
    },
    computed: {
        gifts() {
            return this.$store.state.giftMap;
        },
        giftImage() {
            return this.image;
        },
        dynamicTextSize() {
            const nameLength = this.gift.name ? this.gift.name.length : 0;

            if (nameLength <= 15) {
                return 'text-sm sm:text-base'; // Larger text for short names
            } else if (nameLength <= 25) {
                return 'text-xs sm:text-sm'; // Medium text for medium names
            } else if (nameLength <= 40) {
                return 'text-xs'; // Small text for long names
            } else {
                return 'text-xs'; // Very small text for very long names, will be truncated
            }
        },
        safeUrl() {
            if (!this.gift.url) return '#';
            const url = this.gift.url.trim();
            return url.startsWith('http://') || url.startsWith('https://') ? url : '#';
        }
    },
    watch: {
        isLoading() {
            if (this.isLoading === false) {
                document.querySelector("#imageDiv" + this.gift.id).removeAttribute("hidden")
                document.querySelector("#loadingDiv" + this.gift.id).setAttribute("hidden", true)
            }
        }
    },
    methods: {
        formatDate(dateString) {
            if (!dateString) return 'N/A';
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'N/A';
            return date.toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
            });
        },
        getDomainName(url) {
            try {
                const urlObj = new URL(url);
                let domain = urlObj.hostname;
                if (domain.startsWith('www.')) {
                    domain = domain.slice(4);
                }
                return domain;
            } catch {
                return '';
            }
        },
        getImage(key) {
            if (key.includes("https://")) {
                this.image = key;
            } else if (key == '') {
                this.image = require(`@/assets/gift.png`);
            } else {
                this.image = '/public/' + key;
            };
        },
        async deleteGift() {
            if (confirm("Are you sure you want to delete " + this.gift.name + "?")) {
                try {

                    let giftLinks = await API.graphql({
                        query: getGift,
                        variables: { id: this.gift.id }
                    });

                    try {
                        giftLinks.data.getGift.FriendLink.items.forEach(function (item, index) {
                            const mutationInput = { "id": item.id };
                            API.graphql({ query: deleteGiftFriend, variables: { input: mutationInput }, });
                        });
                    } catch (error) {
                        console.log(error);
                    };

                } catch (error) {
                    console.log('Error deleting gift link...', error)
                };

                try {
                    var response = await API.graphql({ query: deleteGift, variables: { input: { id: this.gift.id } } });
                } catch (error) {
                    console.log('Error deleting gift...', error)
                };
            } else {
                return;
            }

        },
        subscribe() {
            const subToGiftUpdate = `subscription subToGiftUpdate {
        onUpdateGift(filter: {id: {eq: "${this.gift.id}"}}) {
          id
          owner
          image
          price
          description
          url
          name
        }
      }`

            this.giftUpdateSub = API.graphql(
                graphqlOperation(subToGiftUpdate)
            ).subscribe({
                next: (eventData) => {
                    let gift = eventData.value.data.onUpdateGift;
                    this.$store.dispatch("updateGift", gift);
                    this.getImage(gift.image);
                },
            });

            this.giftDeleteSub = API.graphql({
                query: onDeleteGift,
                variables: { owner: this.$store.state.user.data.userid },
            }).subscribe({
                next: (eventData) => {
                    let gift = eventData.value.data.onDeleteGift;
                    this.$store.dispatch("deleteGift", gift);
                },
            });
        },
    },

};
</script>
